// Global Instructions Rule Applied!
//
// The drafts the Commitment forms edit and what a save sends. Three forms
// share them: the Commitment form (a standalone commitment or a pocket), the
// Consolidated commitment form and the New commitment form. Everything here
// is pure: the forms hold the state, these functions turn a draft into the
// payload of fund__save_commitment / fg__save_subscription.
//
// An edit sends only what differs from the loaded record (both RPCs leave an
// absent key alone; null clears). Rates are typed as percentages (2 = 2%) and
// stored as fractions; dates travel as 'YYYY-MM-DD'.

import type { FieldValue } from '../../../core/components/InputManager/types.ts'
import { decimalToPercent, percentToDecimal } from '../../../core/utils/inputNumberFormat.ts'
import type {
  CommitmentFundGroupMember,
  FundCommitmentDetail,
  FundGroupCommitmentDetail
} from '../../../state_manager/data_models/commitment_form.ts'
import type { FundCommitmentSavePayload } from '../../funds/services/fundsService.ts'
import type {
  SubscriptionAllocationInput,
  SubscriptionSavePayload
} from '../../fund_groups/services/fundGroupsService.ts'

/** Amount, status, dates and the GP marker: the same on every commitment kind.
 *  The carry marker is a standalone commitment's alone: never set on a
 *  consolidated commitment, never a pocket's own. */
export interface CommitmentCoreDraft {
  amount: number | null
  status_id: number | null
  date_committed: string | null
  date_closed: string | null
  is_gp_commitment: boolean
  /** the row the fund's carried interest is allocated and paid to */
  is_carry_recipient: boolean
}

/** A standalone commitment's own terms. Rates as percentages (2 = 2%). */
export interface CommitmentTermsDraft {
  management_fee_rate: number | null
  base_carry_rate: number | null
  base_hurdle_multiple: number | null
  stepped_carry_rate: number | null
  stepped_hurdle_multiple: number | null
}

/** Where the additional notice documents come from: the investor's
 *  (overridden false) or the commitment's own set (which may be none). */
export interface CommitmentNoticeDraft {
  overridden: boolean
  template_ids: number[]
}

/** One member fund of a consolidated commitment. The primary fund's amount is
 *  never typed: it is what the satellite funds leave of the commitment. */
export interface AllocationRowDraft {
  memberId: string
  fundId: string
  fundName: string
  isPrimary: boolean
  defaultPct: number | null
  /** the member fund's commitment (pocket), once there is one */
  pocketId: string | null
  amount: number | null
  /** satellite only; null = as the consolidated commitment */
  status_id: number | null
  date_closed: string | null
}

/** A new investor entity created with the commitment. */
export interface NewInvestorDraft {
  name: string | null
  legal_name: string | null
  investor_classification_id: number | null
  sbic_investor_type_id: number | null
}

export const EMPTY_CORE_DRAFT: CommitmentCoreDraft = {
  amount: null,
  status_id: null,
  date_committed: null,
  date_closed: null,
  is_gp_commitment: false,
  is_carry_recipient: false
}

export const EMPTY_TERMS_DRAFT: CommitmentTermsDraft = {
  management_fee_rate: null,
  base_carry_rate: null,
  base_hurdle_multiple: null,
  stepped_carry_rate: null,
  stepped_hurdle_multiple: null
}

export const EMPTY_NOTICE_DRAFT: CommitmentNoticeDraft = { overridden: false, template_ids: [] }

export const EMPTY_NEW_INVESTOR_DRAFT: NewInvestorDraft = {
  name: null,
  legal_name: null,
  investor_classification_id: null,
  sbic_investor_type_id: null
}

/** Form-local field keys (nothing auto-patches; the forms save explicitly). */
export const CommitmentFieldKeys = {
  amount: 'commitment__amount',
  status_id: 'commitment__status_id',
  date_committed: 'commitment__date_committed',
  date_closed: 'commitment__date_closed',
  is_gp_commitment: 'commitment__is_gp_commitment',
  is_carry_recipient: 'commitment__is_carry_recipient',
  management_fee_rate: 'commitment__management_fee_rate',
  base_carry_rate: 'commitment__base_carry_rate',
  base_hurdle_multiple: 'commitment__base_hurdle_multiple',
  stepped_carry_rate: 'commitment__stepped_carry_rate',
  stepped_hurdle_multiple: 'commitment__stepped_hurdle_multiple',
  notice_template_ids: 'commitment__notice_supplement_template_ids',
  account: 'commitment__account',
  investor: 'commitment__investor',
  target: 'commitment__target',
  new_investor_name: 'new_investor__name',
  new_investor_legal_name: 'new_investor__legal_name',
  new_investor_classification: 'new_investor__investor_classification_id',
  new_investor_sbic_type: 'new_investor__sbic_investor_type_id'
} as const

// ---------------------------------------------------------------- value readers

const numberOrNull = (value: FieldValue): number | null => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * The date input hands back its display format (MM/DD/YYYY); the drafts and
 * the RPCs use 'YYYY-MM-DD'. A value that is already ISO (or a timestamp)
 * keeps its date part.
 */
export const toIsoDate = (value: FieldValue): string | null => {
  if (typeof value !== 'string' || value === '') return null
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (us) return `${us[3]}-${us[1]}-${us[2]}`
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null
}

export const sameIds = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const sortedB = [...b].sort((x, y) => x - y)
  return [...a].sort((x, y) => x - y).every((id, i) => id === sortedB[i])
}

/** The ids a multi-select hands back (bare ids or option objects). */
export const toIdList = (value: FieldValue): number[] =>
  Array.isArray(value)
    ? value.map((v) => Number(typeof v === 'object' && v !== null ? v.value : v)).filter((v) => Number.isFinite(v))
    : []

const CORE_KEYS: Record<string, keyof CommitmentCoreDraft> = {
  [CommitmentFieldKeys.amount]: 'amount',
  [CommitmentFieldKeys.status_id]: 'status_id',
  [CommitmentFieldKeys.date_committed]: 'date_committed',
  [CommitmentFieldKeys.date_closed]: 'date_closed',
  [CommitmentFieldKeys.is_gp_commitment]: 'is_gp_commitment',
  [CommitmentFieldKeys.is_carry_recipient]: 'is_carry_recipient'
}

const TERMS_KEYS: Record<string, keyof CommitmentTermsDraft> = {
  [CommitmentFieldKeys.management_fee_rate]: 'management_fee_rate',
  [CommitmentFieldKeys.base_carry_rate]: 'base_carry_rate',
  [CommitmentFieldKeys.base_hurdle_multiple]: 'base_hurdle_multiple',
  [CommitmentFieldKeys.stepped_carry_rate]: 'stepped_carry_rate',
  [CommitmentFieldKeys.stepped_hurdle_multiple]: 'stepped_hurdle_multiple'
}

/** The core draft after one field change; the same object when the key is not a core field. */
export const applyCoreChange = (draft: CommitmentCoreDraft, key: string, value: FieldValue): CommitmentCoreDraft => {
  const field = CORE_KEYS[key]
  if (!field) return draft
  switch (field) {
    case 'amount':
    case 'status_id':
      return { ...draft, [field]: numberOrNull(value) }
    case 'date_committed':
    case 'date_closed':
      return { ...draft, [field]: toIsoDate(value) }
    default:
      // the two markers
      return { ...draft, [field]: value === true }
  }
}

/**
 * Whether an amount can be saved: above zero, or exactly 0 on the commitment
 * that receives the carried interest - the carry's own account.
 * fund__save_commitment holds the same rule; what it checks across the fund's
 * other rows (one recipient per fund) is its alone.
 */
export const commitmentAmountAllowed = (amount: number | null, isCarryRecipient: boolean): boolean =>
  amount != null && (amount > 0 || (amount === 0 && isCarryRecipient))

/** The terms draft after one field change; the same object when the key is not a terms field. */
export const applyTermsChange = (draft: CommitmentTermsDraft, key: string, value: FieldValue): CommitmentTermsDraft => {
  const field = TERMS_KEYS[key]
  return field ? { ...draft, [field]: numberOrNull(value) } : draft
}

// ------------------------------------------------------------- drafts from reads

export const coreFromFundCommitment = (detail: FundCommitmentDetail): CommitmentCoreDraft => ({
  amount: detail.fund_commitments__amount,
  status_id: detail.fund_commitment_statuses__id,
  date_committed: detail.fund_commitments__date_committed,
  date_closed: detail.fund_commitments__date_closed,
  is_gp_commitment: detail.fund_commitments__is_gp_commitment,
  is_carry_recipient: detail.fund_commitments__is_carry_recipient
})

export const termsFromFundCommitment = (detail: FundCommitmentDetail): CommitmentTermsDraft => ({
  management_fee_rate: decimalToPercent(detail.fund_commitments__management_fee_rate),
  base_carry_rate: decimalToPercent(detail.fund_commitments__base_carry_rate),
  base_hurdle_multiple: detail.fund_commitments__base_hurdle_multiple,
  stepped_carry_rate: decimalToPercent(detail.fund_commitments__stepped_carry_rate),
  stepped_hurdle_multiple: detail.fund_commitments__stepped_hurdle_multiple
})

export const noticeFromFundCommitment = (detail: FundCommitmentDetail): CommitmentNoticeDraft => ({
  overridden: detail.fund_commitments__notice_supplements_overridden,
  template_ids: [...detail.prog__notice_supplement_template_ids]
})

export const coreFromConsolidated = (detail: FundGroupCommitmentDetail): CommitmentCoreDraft => ({
  amount: detail.fund_group_commitments__amount,
  status_id: detail.fund_commitment_statuses__id,
  date_committed: detail.fund_group_commitments__date_committed,
  date_closed: detail.fund_group_commitments__date_closed,
  is_gp_commitment: detail.fund_group_commitments__is_gp_commitment,
  // the carry marker (phase 5 part 1): the row every member fund's carried
  // interest goes to, which may be a commitment of 0
  is_carry_recipient: detail.fund_group_commitments__is_carry_recipient
})

export const noticeFromConsolidated = (detail: FundGroupCommitmentDetail): CommitmentNoticeDraft => ({
  overridden: detail.prog__notice_supplements_overridden,
  template_ids: [...detail.prog__notice_supplement_template_ids]
})

export const allocationsFromConsolidated = (detail: FundGroupCommitmentDetail): AllocationRowDraft[] =>
  detail.prog__allocations.map((a) => ({
    memberId: a.fund_group_members__id,
    fundId: a.funds__id,
    fundName: a.funds__name ?? '',
    isPrimary: a.fund_group_members__is_primary,
    defaultPct: a.fund_group_members__default_allocation_pct,
    pocketId: a.fund_commitments__id,
    amount: a.fund_group_commitment_allocations__amount,
    status_id: a.fund_group_commitment_allocations__status_id,
    date_closed: a.fund_group_commitment_allocations__date_closed
  }))

/** A new consolidated commitment starts with everything in the primary fund. */
export const allocationsFromMembers = (members: CommitmentFundGroupMember[]): AllocationRowDraft[] =>
  members.map((m) => ({
    memberId: m.fund_group_members__id,
    fundId: m.funds__id,
    fundName: m.funds__name ?? '',
    isPrimary: m.fund_group_members__is_primary,
    defaultPct: m.fund_group_members__default_allocation_pct,
    pocketId: null,
    amount: null,
    status_id: null,
    date_closed: null
  }))

// ------------------------------------------------------------------ allocations

const round2 = (value: number): number => Math.round(value * 100) / 100

export interface AllocationSummary {
  /** what is allocated to the satellite funds */
  satelliteSum: number
  /** what stays with the primary fund (must be positive) */
  residual: number
  /** satellite amounts that are not effective yet (not Closed, or closing in the future) */
  pendingSum: number
}

/**
 * The split of a consolidated commitment. Until a satellite allocation is
 * effective (its status, or the commitment's when it has none, is Closed and
 * its close date is not in the future) its amount still counts on the primary
 * fund for calls, fees and closed capital.
 */
export const summariseAllocations = (
  rows: AllocationRowDraft[],
  core: CommitmentCoreDraft,
  closedStatusId: number | null,
  today: string
): AllocationSummary => {
  let satelliteSum = 0
  let pendingSum = 0
  rows.forEach((row) => {
    if (row.isPrimary) return
    const amount = row.amount ?? 0
    if (amount <= 0) return
    satelliteSum += amount
    const effectiveStatus = row.status_id ?? core.status_id
    const effectiveDate = row.date_closed ?? core.date_closed
    const notClosed = closedStatusId != null && effectiveStatus !== closedStatusId
    const futureDate = effectiveDate != null && effectiveDate > today
    if (notClosed || futureDate) pendingSum += amount
  })
  return {
    satelliteSum: round2(satelliteSum),
    residual: round2((core.amount ?? 0) - satelliteSum),
    pendingSum: round2(pendingSum)
  }
}

/** The full allocation set a save sends: the satellite rows that carry an
 *  amount, and the primary fund with what they leave. */
export const buildAllocationInputs = (rows: AllocationRowDraft[], amount: number): SubscriptionAllocationInput[] => {
  const satellites = rows
    .filter((row) => !row.isPrimary && (row.amount ?? 0) > 0)
    .map((row) => ({
      fund_group_member_id: row.memberId,
      amount: row.amount ?? 0,
      status_id: row.status_id,
      date_closed: row.date_closed
    }))
  const primary = rows.find((row) => row.isPrimary)
  const residual = round2(amount - satellites.reduce((sum, a) => sum + a.amount, 0))
  return primary ? [...satellites, { fund_group_member_id: primary.memberId, amount: residual }] : satellites
}

/** A satellite row as the save sees it (an empty amount is "not allocated"). */
const satelliteState = (row: AllocationRowDraft): string =>
  (row.amount ?? 0) > 0 ? `${row.memberId}|${row.amount}|${row.status_id ?? ''}|${row.date_closed ?? ''}` : ''

export const allocationsChanged = (rows: AllocationRowDraft[], baseline: AllocationRowDraft[]): boolean => {
  const state = (list: AllocationRowDraft[]) =>
    list
      .filter((row) => !row.isPrimary)
      .map(satelliteState)
      .filter(Boolean)
      .sort()
      .join(';')
  return state(rows) !== state(baseline)
}

// --------------------------------------------------------------------- payloads

type NoticeKeys = Pick<FundCommitmentSavePayload, 'notice_supplements_overridden' | 'notice_supplement_template_ids'>

const noticeChanged = (draft: CommitmentNoticeDraft, baseline: CommitmentNoticeDraft): boolean =>
  draft.overridden !== baseline.overridden || (draft.overridden && !sameIds(draft.template_ids, baseline.template_ids))

/** false = the investor's documents (the commitment's own are cleared);
 *  true = exactly the set given, which may be none. */
const noticeKeys = (draft: CommitmentNoticeDraft): NoticeKeys =>
  draft.overridden
    ? { notice_supplements_overridden: true, notice_supplement_template_ids: draft.template_ids }
    : { notice_supplements_overridden: false }

const termsPayload = (terms: CommitmentTermsDraft) => ({
  management_fee_rate: percentToDecimal(terms.management_fee_rate),
  base_carry_rate: percentToDecimal(terms.base_carry_rate),
  base_hurdle_multiple: terms.base_hurdle_multiple,
  stepped_carry_rate: percentToDecimal(terms.stepped_carry_rate),
  stepped_hurdle_multiple: terms.stepped_hurdle_multiple
})

export interface StandaloneDraft {
  core: CommitmentCoreDraft
  terms: CommitmentTermsDraft
  notice: CommitmentNoticeDraft
}

const coreChanged = (draft: CommitmentCoreDraft, baseline: CommitmentCoreDraft): boolean =>
  draft.amount !== baseline.amount ||
  draft.status_id !== baseline.status_id ||
  draft.date_committed !== baseline.date_committed ||
  draft.date_closed !== baseline.date_closed ||
  draft.is_gp_commitment !== baseline.is_gp_commitment ||
  draft.is_carry_recipient !== baseline.is_carry_recipient

const termsChanged = (draft: CommitmentTermsDraft, baseline: CommitmentTermsDraft): boolean =>
  (Object.keys(draft) as Array<keyof CommitmentTermsDraft>).some((key) => draft[key] !== baseline[key])

/**
 * Whether the form holds unsaved changes. Not the same as "the payload has
 * keys": a required field that was emptied is a change the user must be able
 * to Save (and be told about) or Cancel, though an empty amount is never sent.
 */
export const standaloneDraftChanged = (draft: StandaloneDraft, baseline: StandaloneDraft): boolean =>
  coreChanged(draft.core, baseline.core) ||
  termsChanged(draft.terms, baseline.terms) ||
  noticeChanged(draft.notice, baseline.notice)

/** What an edit of a standalone commitment sends: only what changed. */
export const buildStandaloneEditPayload = (
  id: string,
  draft: StandaloneDraft,
  baseline: StandaloneDraft
): FundCommitmentSavePayload => {
  const payload: FundCommitmentSavePayload = { id }
  if (draft.core.amount !== baseline.core.amount && draft.core.amount != null) payload.amount = draft.core.amount
  if (draft.core.status_id !== baseline.core.status_id && draft.core.status_id != null) {
    payload.status_id = draft.core.status_id
  }
  if (draft.core.date_committed !== baseline.core.date_committed) payload.date_committed = draft.core.date_committed
  if (draft.core.date_closed !== baseline.core.date_closed) payload.date_closed = draft.core.date_closed
  if (draft.core.is_gp_commitment !== baseline.core.is_gp_commitment) {
    payload.is_gp_commitment = draft.core.is_gp_commitment
  }
  if (draft.core.is_carry_recipient !== baseline.core.is_carry_recipient) {
    payload.is_carry_recipient = draft.core.is_carry_recipient
  }
  const next = termsPayload(draft.terms)
  const prev = termsPayload(baseline.terms)
  ;(Object.keys(next) as Array<keyof typeof next>).forEach((key) => {
    if (next[key] !== prev[key]) payload[key] = next[key]
  })
  if (noticeChanged(draft.notice, baseline.notice)) Object.assign(payload, noticeKeys(draft.notice))
  return payload
}

/** A pocket of a consolidated commitment: its two markers are its own - the GP
 *  marker, and (owner 2026-10-12) the carry marker, since the row a fund's
 *  carried interest goes to is chosen per member fund. */
export const buildPocketEditPayload = (
  id: string,
  draft: CommitmentCoreDraft,
  baseline: CommitmentCoreDraft
): FundCommitmentSavePayload => {
  const payload: FundCommitmentSavePayload = { id }
  if (draft.is_gp_commitment !== baseline.is_gp_commitment) payload.is_gp_commitment = draft.is_gp_commitment
  if (draft.is_carry_recipient !== baseline.is_carry_recipient) payload.is_carry_recipient = draft.is_carry_recipient
  return payload
}

export interface ConsolidatedDraft {
  core: CommitmentCoreDraft
  notice: CommitmentNoticeDraft
  allocations: AllocationRowDraft[]
}

/** Whether the form holds unsaved changes (see standaloneDraftChanged). */
export const consolidatedDraftChanged = (draft: ConsolidatedDraft, baseline: ConsolidatedDraft): boolean =>
  coreChanged(draft.core, baseline.core) ||
  noticeChanged(draft.notice, baseline.notice) ||
  allocationsChanged(draft.allocations, baseline.allocations)

/**
 * What an edit of a consolidated commitment sends: only what changed. The
 * allocation set goes along whenever the amount or a satellite row changed
 * (the primary fund then takes what the satellites leave); a payload of only
 * the two markers and / or the notice documents changes nothing else. A
 * commitment of 0 (the carry's own account) sends no allocation set: the
 * save derives one allocation of 0 per member fund.
 */
export const buildConsolidatedEditPayload = (
  id: string,
  draft: ConsolidatedDraft,
  baseline: ConsolidatedDraft
): SubscriptionSavePayload => {
  const payload: SubscriptionSavePayload = { id }
  const amountChanged = draft.core.amount !== baseline.core.amount && draft.core.amount != null
  if (amountChanged && draft.core.amount != null) payload.amount = draft.core.amount
  if (draft.core.status_id !== baseline.core.status_id) payload.status_id = draft.core.status_id
  if (draft.core.date_committed !== baseline.core.date_committed) payload.date_committed = draft.core.date_committed
  if (draft.core.date_closed !== baseline.core.date_closed) payload.date_closed = draft.core.date_closed
  if (draft.core.is_gp_commitment !== baseline.core.is_gp_commitment) {
    payload.is_gp_commitment = draft.core.is_gp_commitment
  }
  if (draft.core.is_carry_recipient !== baseline.core.is_carry_recipient) {
    payload.is_carry_recipient = draft.core.is_carry_recipient
  }
  if (noticeChanged(draft.notice, baseline.notice)) Object.assign(payload, noticeKeys(draft.notice))
  if (
    (amountChanged || allocationsChanged(draft.allocations, baseline.allocations)) &&
    draft.core.amount != null &&
    draft.core.amount > 0
  ) {
    payload.allocations = buildAllocationInputs(draft.allocations, draft.core.amount)
  }
  return payload
}

/** Who a new commitment is for: an existing investor entity, or a new one
 *  created under the account in the same save. */
export type InvestorChoice =
  | { investorId: string }
  | { accountId: string; newInvestor: NewInvestorDraft }

const text = (value: string | null): string | null => {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

/** The investor keys of a save: an existing investor, or a new entity under the account. */
export const investorKeys = (
  choice: InvestorChoice
): Pick<FundCommitmentSavePayload, 'investor_id' | 'new_investor'> =>
  'investorId' in choice
    ? { investor_id: choice.investorId }
    : {
        new_investor: {
          account_id: choice.accountId,
          name: text(choice.newInvestor.name) ?? '',
          legal_name: text(choice.newInvestor.legal_name),
          investor_classification_id: choice.newInvestor.investor_classification_id,
          sbic_investor_type_id: choice.newInvestor.sbic_investor_type_id
        }
      }

const coreCreateKeys = (core: CommitmentCoreDraft) => ({
  amount: core.amount ?? 0,
  status_id: core.status_id ?? undefined,
  date_committed: core.date_committed,
  date_closed: core.date_closed,
  ...(core.is_gp_commitment ? { is_gp_commitment: true } : {}),
})

/** What creating a standalone commitment sends. The carry marker goes along
 *  only when it is on. */
export const buildStandaloneCreatePayload = (
  fundId: string,
  choice: InvestorChoice,
  draft: StandaloneDraft
): FundCommitmentSavePayload => ({
  fund_id: fundId,
  ...investorKeys(choice),
  ...coreCreateKeys(draft.core),
  ...(draft.core.is_carry_recipient ? { is_carry_recipient: true } : {}),
  ...termsPayload(draft.terms),
  ...(draft.notice.overridden ? noticeKeys(draft.notice) : {})
})

/** What creating a consolidated commitment sends. Without a satellite amount
 *  no allocation set is sent: everything starts in the primary fund. The
 *  carry marker goes along only when it is on (a commitment of 0 is then
 *  allowed; the save gives every member fund a pocket of 0). */
export const buildConsolidatedCreatePayload = (
  fundGroupId: string,
  choice: InvestorChoice,
  draft: ConsolidatedDraft
): SubscriptionSavePayload => {
  const hasSatellite = draft.allocations.some((row) => !row.isPrimary && (row.amount ?? 0) > 0)
  return {
    fund_group_id: fundGroupId,
    ...investorKeys(choice),
    ...coreCreateKeys(draft.core),
    ...(draft.core.is_carry_recipient ? { is_carry_recipient: true } : {}),
    ...(draft.notice.overridden ? noticeKeys(draft.notice) : {}),
    ...(hasSatellite && draft.core.amount != null
      ? { allocations: buildAllocationInputs(draft.allocations, draft.core.amount) }
      : {})
  }
}
